import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CircleMinusIcon } from '@icons/CircleMinusIcon'
import { ThreeDotIsVerticalIcon } from '@icons/ThreeDotIsVerticalIcon'

import { Body1 } from '@ui/Typography'
import { Dropdown } from '@ui/v2/Dropdown/Dropdown'
import { DropdownCard } from '@ui/v2/Dropdown/DropdownCard'
import { DropdownList } from '@ui/v2/Dropdown/DropdownList'
import { DropdownListItem } from '@ui/v2/Dropdown/DropdownListItem'
import { EditIcon } from '@ui/v2/Icons/EditIcon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'

import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '@model/NameDto'

import { Button } from '@global/button/Button'
import IconButton from '@global/iconButton/IconButton'

import { useTranslatedRebateOptions } from './useTranslatedRebateOptions'

import styles from './GroupsPageTable.module.scss'

interface GroupsPageTableProps {
  rebateGroups: IntroducingBrokerRebateGroupDto[]
  sortOptions?: NameDto<keyof IntroducingBrokerRebateGroupDto | null>[]
  pageQuery: PageQuery | undefined
  sticky?: 'left'
  setPageQuery?: (value: PageQuery) => void
  onEdit: (group: IntroducingBrokerRebateGroupDto) => void
  onDelete: (group: IntroducingBrokerRebateGroupDto) => void
}

export const GroupsPageTable: FC<GroupsPageTableProps> = (props) => {
  const { rebateGroups, sortOptions, pageQuery, sticky, setPageQuery, onEdit, onDelete } = props
  const { translateApprovalTypes, translatePeriods } = useTranslatedRebateOptions()
  return (
    <Table sticky={sticky} className={styles.table}>
      <TableHeader>
        <TableRow>
          {sortOptions?.map((option) =>
            option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                sort={pageQuery?.sort || undefined}
                sortOrder={pageQuery?.sortOrder}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({
                    ...pageQuery,
                    sort,
                    sortOrder,
                  })
                }}
              >
                {option.name}
              </TableHeadSort>
            ) : (
              <TableHead key={option.name}>{option.name}</TableHead>
            )
          )}
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rebateGroups.map((group) => (
          <TableRow key={group.id}>
            <TableCell width={260}>{group.name}</TableCell>
            <TableCell width={260}>
              {translateApprovalTypes(group.rebateApprovalType).name}
            </TableCell>
            <TableCell width={260}>{translatePeriods(group.rebatePeriod).name}</TableCell>
            <TableCell width={260}>{group.tradingAccountsCount}</TableCell>
            <TableCell width={140}>{group.rebateAmountInPercentage}%</TableCell>
            <TableCell align='right' className={styles.rowActions}>
              <RowActions onEdit={() => onEdit(group)} onDelete={() => onDelete(group)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

interface RowActionsProps {
  onEdit: () => void
  onDelete: () => void
}

export const RowActions: FC<RowActionsProps> = (props) => {
  const { onEdit, onDelete } = props

  const { t } = useTranslation()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const actions = [
    {
      label: t('Edit'),
      onClick: () => {
        onEdit()
        setIsDropdownOpen(false)
      },
      icon: EditIcon,
    },
    {
      label: t('Delete'),
      onClick: () => {
        onDelete()
        setIsDropdownOpen(false)
      },
      icon: CircleMinusIcon,
    },
  ]

  return (
    <Dropdown
      direction='topLeft'
      isOpen={isDropdownOpen}
      setIsOpen={setIsDropdownOpen}
      content={
        <DropdownCard>
          <DropdownList>
            {actions?.map((action) => {
              return (
                <DropdownListItem
                  key={action.label}
                  onClick={action.onClick}
                  startIcon={action.icon}
                >
                  {action.label}
                </DropdownListItem>
              )
            })}
          </DropdownList>
          <div className={styles.actionsFooter}>
            <Button.New
              appearance='secondary'
              onClick={() => setIsDropdownOpen(false)}
              size='L'
              fullWidth
            >
              {t('Cancel')}
            </Button.New>
          </div>
        </DropdownCard>
      }
    >
      <IconButton.New appearance='ghost'>
        <ThreeDotIsVerticalIcon />
      </IconButton.New>
    </Dropdown>
  )
}

interface EmptyGroupsPageTableProps {
  onCreateClick: () => void
  isAnyFilterActive?: boolean
}

export const EmptyGroupsPageTable: FC<EmptyGroupsPageTableProps> = (props) => {
  const { onCreateClick, isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={
        isAnyFilterActive
          ? t('IB.RebatesPage.GroupsPage.Nothing to show')
          : t('IB.RebatesPage.GroupsPage.No groups to display')
      }
      content={
        isAnyFilterActive ? (
          t('IB.RebatesPage.GroupsPage.Please, adjust your filters')
        ) : (
          <Body1>
            {t('IB.RebatesPage.GroupsPage.Create your first group')}{' '}
            <Button appearance='link' className={styles.clickable} onClick={onCreateClick}>
              <Body1 lowercase>{t('IB.RebatesPage.GroupsPage.Here')}</Body1>
            </Button>
          </Body1>
        )
      }
    />
  )
}
